// Dependencies
import React from "react";
import ReactDOM from "react-dom/client";

// MSAL
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./config/authConfig";

// Components
import App from "./App";

// Styles
import "styles/index.css";
import { validateEnv } from "./utils/validateEnv";
import { loggerService } from "./services/logger";

import "@/config/appInsights";

// Zero-dependency CSP nonce shim for runtime-injected <style> tags (e.g. MUI/Emotion).
const nonce = document
  .querySelector('meta[name="csp-nonce"]')
  ?.getAttribute("content");

if (nonce && nonce !== "__CSP_NONCE__") {
  const originalCreateElement = document.createElement.bind(document);

  document.createElement = function patchedCreateElement(
    tagName: string,
    options?: ElementCreationOptions,
  ): HTMLElement {
    const el = originalCreateElement(tagName, options) as HTMLElement;
    if (typeof tagName === "string" && tagName.toLowerCase() === "style") {
      el.setAttribute("nonce", nonce);
    }
    return el;
  } as typeof document.createElement;
}

// Validate environment variables before starting the app
validateEnv();

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export const msalContext = App.msalContext;
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and handle redirect promise before rendering
msalInstance
  .initialize()
  .then(() => {
    // Handle redirect promise to process auth response from redirect
    return msalInstance.handleRedirectPromise();
  })
  .then((response) => {
    // Set active account after handling redirect
    const accounts = msalInstance.getAllAccounts();
    if (response && response.account) {
      msalInstance.setActiveAccount(response.account);
    } else if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    // Set up event callback for login events
    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        "account" in event.payload
      ) {
        const account = event.payload.account;
        if (account) {
          msalInstance.setActiveAccount(account);
        }

        // this is the state we initially sent to azure before sign in; azure sends it back after sign in
        const state = event.payload?.state;
        if (state) {
          sessionStorage.setItem("msalState", state);
        }
      }

      // if (event.eventType === EventType.LOGIN_FAILURE) {
      //   loggerService.trackException(event as unknown as Error, {
      //     details: "Login failed",
      //   });
      // }
    });

    // Render the app after MSAL is initialized and redirect is handled
    ReactDOM.createRoot(
      document.getElementById("root") || document.createElement("div"),
    ).render(
      <React.StrictMode>
        <App instance={msalInstance} />
      </React.StrictMode>,
    );
  })
  .catch((error) => {
    console.error("MSAL initialization error:", error);
    loggerService.trackException(error, { details: "MSAL initialization" });
  });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();